import type { IntlShape } from 'react-intl';

import { uniq } from 'lodash';

import { ContentAst } from '~/eds/src/types/ContentAst';
import { Entity } from '~/eds/src/types/Entity';
import { PilotId } from '~/eds/src/types/PilotId';
import { User as EdsUser } from '~/eds/src/types/User';
import { extractMentionEntities } from '~/eds/src/utils/contentAst';
import { testHasTextMatch } from '~/eds/src/utils/testHasTextMatch';
import { EntityType } from '~/enums/EntityType';
import {
  Comment as EdsComment,
  EnabledFilters,
} from '~/features/comments/types';
import { Comment, CommentFilters, ContextEntityType } from '~/types/Comment';
import { Nullable } from '~/types/Nullable';
import { User } from '~/types/User';
import { Uuid } from '~/types/Uuid';
import { getUserNameFromFullname } from '~/utils/user';

interface TransformOptions {
  users: Record<PilotId, EdsUser>;
  serverComments: Comment[];
  intl: IntlShape;
}

export const toEditorDisplayDate = (date: string | Date): Date => {
  const parsedDate = new Date(date);
  return new Date(
    parsedDate.getTime() + parsedDate.getTimezoneOffset() * 60 * 1000,
  );
};

export const resolveImportedCommentDates = <
  T extends {
    createdDate: Date;
    modifiedDate: Date;
    createdBy: number | null;
    creatorName?: string | null;
    createdDateIsUtc?: boolean;
  },
>(
  comment: T,
): T => {
  if (
    comment.createdDateIsUtc ||
    comment.createdBy != null ||
    !comment.creatorName
  ) {
    return comment;
  }
  return {
    ...comment,
    createdDate: toEditorDisplayDate(comment.createdDate),
  };
};

const getCommentCreatedBy = (
  comment: Comment,
  users: Record<PilotId, EdsUser>,
  intl: IntlShape,
): EdsUser => {
  if (comment.createdBy) {
    const creator = users[comment.createdBy];

    if (creator) return creator;
    return { id: comment.createdBy, firstName: '', lastName: '', email: '' };
  }

  if (!comment.createdBy && comment.creatorName) {
    const { firstName, lastName } = getUserNameFromFullname(
      comment.creatorName,
    );
    return {
      id: '',
      firstName,
      lastName: intl.formatMessage(
        {
          id: 'CommentPanel.externalUser',
          defaultMessage: `{lastName} (External User)`,
          description:
            'Label for external user in comment panel. Example: lastName = "Smith"',
        },
        {
          lastName,
        },
      ),
      email: intl.formatMessage({
        id: 'CommentPanel.externalUserEmail',
        defaultMessage: 'External user',
        description: 'Email for external user in comment panel',
      }),
    };
  }

  return { id: comment.createdBy!, firstName: '', lastName: '', email: '' };
};

const getCommentCreatedDate = (comment: Comment) => {
  // this will only be applied to comments that are embedded in the document and imported from the uploaded document
  if (comment.isCommentEmbedded && comment.creatorName && !comment.createdBy) {
    return toEditorDisplayDate(comment.createdDate.toISOString());
  }
  return comment.createdDate;
};

export const transformServerComments =
  ({ users, serverComments, intl }: TransformOptions) =>
  (comment: Comment) => ({
    ...comment,
    createdBy: getCommentCreatedBy(comment, users, intl),
    createdDate: getCommentCreatedDate(comment),
    //TODO: not sure if this data will be on BE, so let's do it here for now.
    replies: serverComments.filter(
      (filteredComment) => filteredComment.threadId === comment.id,
    ).length,
  });

export const resolveCommentAuthorName =
  (commentAuthorNameResolver?: (comment: Comment) => string) =>
  (comment: Comment) => {
    if (
      !comment.createdBy &&
      !comment.creatorName &&
      commentAuthorNameResolver
    ) {
      return {
        ...comment,
        creatorName: commentAuthorNameResolver(comment),
      };
    }
    return comment;
  };

export const resolveIsCommentEmbedded =
  (isCommentEmbeddedResolver?: (comment: Comment) => boolean) =>
  (comment: Comment) => {
    if (isCommentEmbeddedResolver) {
      return {
        ...comment,
        isCommentEmbedded: isCommentEmbeddedResolver(comment),
      };
    }
    return comment;
  };

export const filterByParentComment =
  (parentComment: Nullable<EdsComment>) => (comment: Comment) =>
    comment.threadId === (parentComment?.id || null);

export const filterHighlightsOfDifferentVersion = (
  comment: EdsComment,
  entity: Entity,
) => {
  const hasHighlight = comment.context.some((commentContext) =>
    (
      [EntityType.Highlight, EntityType.EditorHighlight] as ContextEntityType[]
    ).includes(commentContext.type),
  );
  if (hasHighlight) {
    return comment.context.some(
      (commentContext) =>
        commentContext.type === entity.type && commentContext.id === entity.id,
    );
  }
  return true;
};

export const transformEdsCommentToComment = (
  edsComment: EdsComment,
): Comment => {
  return {
    ...edsComment,
    createdBy: edsComment.createdBy.id,
  };
};

interface FilterCommentOptions {
  filters: CommentFilters;
  currentUser: User;
  parentComment?: Nullable<EdsComment>;
}

export const getEnabledFilters = ({
  enabledFilters,
  filters,
}: {
  enabledFilters: EnabledFilters;
  filters: CommentFilters;
}) => {
  const initialFilters = { ...filters };
  if (!enabledFilters.flags) {
    initialFilters.flags = [];
  }
  if (!enabledFilters.source) {
    initialFilters.source = 'all';
  }
  if (!enabledFilters.status) {
    initialFilters.status = 'open';
  }
  return initialFilters;
};

const filterWithFlags = ({
  filters,
  currentUser,
  comment,
}: {
  currentUser: User;
  filters: CommentFilters;
  comment: Comment;
}) => {
  if (filters.flags.length !== 0) {
    for (const flag of filters.flags) {
      if (flag === 'byMe' && comment.createdBy === currentUser.id) {
        return true;
      }
      if (flag === 'mentionMe') {
        const userId: Uuid = String(currentUser.id);
        const entities = extractMentionEntities(comment.content, {
          type: 'user',
          id: userId,
        });
        if (entities.has(userId)) {
          return true;
        }
      }
    }
    return false;
  } else {
    return true;
  }
};

const filterWithStatus = ({
  filters,
  comment,
}: {
  filters: CommentFilters;
  comment: Comment;
}) => {
  if (
    filters.status === 'all' ||
    (filters.status === 'open' && !comment.isResolved) ||
    (filters.status === 'isResolved' && comment.isResolved)
  ) {
    return true;
  }
  return false;
};

const filterWithSource = ({
  filters,
  comment,
}: {
  filters: CommentFilters;
  comment: Comment;
}) => {
  if (filters.source === 'all') return true;
  const commentSource = comment.context.some((context) =>
    [EntityType.EditorHighlight, EntityType.Highlight].includes(
      context.type as EntityType,
    ),
  )
    ? 'document'
    : 'ticket';
  if (filters.source && commentSource === filters.source) {
    return true;
  }
  return false;
};

export const filterComments =
  ({ filters, currentUser, parentComment }: FilterCommentOptions) =>
  (comment: Comment) => {
    if (!parentComment && comment.threadId !== null) return true;

    const matchesAFilter = filterWithFlags({ filters, currentUser, comment });
    const matchesStatus = filterWithStatus({ filters, comment });
    const matchesSource = filters.source
      ? filterWithSource({ filters, comment })
      : true;

    return matchesAFilter && matchesStatus && matchesSource;
  };

export const searchComments = (search: string) => (comment: Comment) =>
  search
    ? testHasTextMatch(serializeContentAst(comment.content), search)
    : comment;

const serializeContentAst = (contentArr: ContentAst) =>
  contentArr.reduce((text, content) => {
    if ('text' in content) {
      return text + content.text;
    }

    if ('children' in content) {
      const childText: string = serializeContentAst(content.children);
      return text + childText;
    }

    return text;
  }, '');

export const extractUserMentionsFromComments = (comments: Comment[]) => {
  const userIds: PilotId[] = [];
  for (const comment of comments) {
    const userMentions = extractMentionEntities(comment.content, {
      type: 'user',
      id: '',
    });

    userIds.push(...Array.from(userMentions));
  }

  return uniq(userIds);
};
